/**
 * @file pages/LogoutPage.js
 * @description Branded sign-out screen shown after logout.
 * Shows features, invites user back in, feels premium.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: '📋',
    title: 'Inspection Templates',
    desc: 'Build custom templates for any inspection type. Home, septic, well, commercial — your workflow, your way.'
  },
  {
    icon: '📸',
    title: 'Photo Documentation',
    desc: 'Capture, annotate, and attach photos directly to inspection items. Evidence that holds up.'
  },
  {
    icon: '📄',
    title: 'Professional Reports',
    desc: 'One tap generates a polished PDF with cover page, deficiency summary, and full item breakdown.'
  },
  {
    icon: '📶',
    title: 'Works Offline',
    desc: 'Basements. Rural properties. Dead zones. Inspection Butler works where your signal doesn\'t.'
  },
  {
    icon: '🔧',
    title: 'Built by an Inspector',
    desc: 'Not designed in a boardroom. Built by someone who actually crawls under houses for a living.'
  },
  {
    icon: '🚀',
    title: 'More Coming Fast',
    desc: 'Scheduling, invoicing, payments, client portal, and field tools — all in one place. Watch this space.'
  }
];

export default function LogoutPage() {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#000',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif'
    }}>

      {/* Hero */}
      <div style={{
        textAlign: 'center',
        padding: '80px 24px 60px',
        maxWidth: '640px'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔍</div>
        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700,
          margin: '0 0 16px',
          letterSpacing: '-0.03em',
          background: 'linear-gradient(135deg, #fff 0%, #aaa 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          You're signed out.
        </h1>
        <p style={{
          fontSize: '1.2rem',
          color: '#888',
          margin: '0 0 40px',
          lineHeight: 1.5
        }}>
          Your inspections are here when you need them.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/login" style={{
            display: 'inline-block',
            padding: '14px 36px',
            backgroundColor: '#fff',
            color: '#000',
            borderRadius: '980px',
            fontWeight: 600,
            fontSize: '1rem',
            textDecoration: 'none',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={e => e.target.style.opacity = '0.85'}
          onMouseLeave={e => e.target.style.opacity = '1'}>
            Sign Back In
          </Link>
          <Link to="/build-log" style={{
            display: 'inline-block',
            padding: '14px 36px',
            backgroundColor: 'transparent',
            color: '#fff',
            borderRadius: '980px',
            fontWeight: 600,
            fontSize: '1rem',
            textDecoration: 'none',
            border: '1px solid #333',
            transition: 'border-color 0.2s'
          }}
          onMouseEnter={e => e.target.style.borderColor = '#666'}
          onMouseLeave={e => e.target.style.borderColor = '#333'}>
            Watch Us Build It
          </Link>
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: '100%', maxWidth: '900px', height: '1px', backgroundColor: '#1a1a1a', margin: '0 24px' }} />

      {/* Feature grid */}
      <div style={{
        width: '100%',
        maxWidth: '900px',
        padding: '60px 24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '24px'
      }}>
        {features.map((f, i) => (
          <div key={i} style={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #1a1a1a',
            borderRadius: '16px',
            padding: '28px 24px',
            transition: 'border-color 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#333'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#1a1a1a'}>
            <div style={{ fontSize: '1.8rem', marginBottom: '12px' }}>{f.icon}</div>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              margin: '0 0 8px',
              color: '#fff'
            }}>{f.title}</h3>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              margin: 0,
              lineHeight: 1.6
            }}>{f.desc}</p>
          </div>
        ))}
      </div>

      {/* Bottom tagline */}
      <div style={{
        textAlign: 'center',
        padding: '40px 24px 60px',
        borderTop: '1px solid #1a1a1a',
        width: '100%'
      }}>
        <p style={{ color: '#444', fontSize: '0.85rem', margin: 0 }}>
          Inspection Butler · Built in Weaverville, NC · inspectionbutler.com
        </p>
      </div>

    </div>
  );
}
